import { useEffect, useRef } from 'react';
import { useRelayEnvironment } from 'react-relay';

import { getObjectFromRelayStoreById } from '../functions';
import type { TasksQuery$variables } from '../graphql/__generated__/TasksQuery.graphql';
import useDynamicSelector from '../hooks/useDynamicSelector';
import useGraphQLLoader, { IGraphQLLoader, IGraphQLTableLoader } from '../hooks/useGraphQLLoader';
import { TableColumnSmxType } from '../types';
import { getStructureColumn } from './functions';

const getRowWithData = (columnsList: TableColumnSmxType[], dataRow: any, level: string) => {
    const newRow: { [key: string]: string } = {
        key: `${dataRow.id}_${level}`,
        keyRaw: dataRow.id,
    };

    for (const column of columnsList) {
        // if (column.children) {
        //     createCell(column.children, dataRow);
        //     continue;
        // }

        const arrayNameField = column.dataIndex.split('.');

        const newValue: string[] = [];

        const getValue = (value: any, index: number) => {
            if (!value) {
                return;
            }

            const field = arrayNameField[index];
            const isLast = index === arrayNameField.length - 1;
            const newIndex = index + 1;

            if (Array.isArray(value)) {
                for (const val of value) {
                    if (isLast) {
                        newValue.push(val[field]);
                    }
                    getValue(val[field], newIndex);
                }
            } else {
                if (isLast) {
                    newValue.push(value[field]);
                }
                getValue(value[field], newIndex);
            }
        };

        getValue(dataRow, 0);

        newRow[column.dataIndex] = newValue.join(', ') || '';
    }
    return newRow;
};

const getChildrenByExtendRow = (dataRow: any, index: number, extendRow: any, extendRowByField: any, columns: any[]) => {
    if (!extendRow) {
        return null;
    }

    const childrenRow = [];

    if (extendRowByField && extendRowByField[index]) {
        for (const field of extendRowByField[index]) {
            const selectedListSubRowByField = dataRow[field];

            if (!selectedListSubRowByField) {
                continue;
            }

            for (const subRowByField of selectedListSubRowByField) {
                const subNewRowByField = getRowWithData(columns, subRowByField, `${index}`);
                childrenRow.push(subNewRowByField);
            }
        }
    }

    const selectedListSubRow = dataRow[extendRow[index]];
    if (selectedListSubRow) {
        for (const subRow of selectedListSubRow) {
            const subNewRow = getRowWithData(columns, subRow, `${index}`);
            const subNewChildren = getChildrenByExtendRow(subRow, index + 1, extendRow, extendRowByField, columns);

            if (subNewChildren) {
                (subNewRow as any).children = subNewChildren;
            }

            childrenRow.push(subNewRow);
        }

        return childrenRow;
    }

    if (childrenRow.length) {
        return childrenRow;
    }

    return null;
};

const TableDbLoader = <T extends TasksQuery$variables, K, E>({
    query,
    fragmentList,
    fragment,
    fragmentConnection,
    nameQuery,
    columnsLabel,
    paramSorter = [],
    isAddColumnAction = false,
    onUpdateData,
    varRequest,
    extendRow,
    extendRowByField,
    externalFetchKey,
    fixedColumn,
}: IGraphQLTableLoader<T, K, E> & { isAddColumnAction?: boolean }) => {
    const environment = useRelayEnvironment();

    const prefixFetchKey = useDynamicSelector('table', 'prefixFetchKey');
    const prefixFetchKeyUpdate = useDynamicSelector('table', 'prefixFetchKeyUpdate');
    const dataRowsRef = useRef<any[]>([]);
    const dataStructureRef = useRef<any[]>([]);

    const paramsLoader: IGraphQLLoader<T, E> = {
        varRequest: varRequest || undefined,
        query,
        nameQuery,
        fragmentList,
        fragment,
        fragmentConnection,
        prefixFetchKey,
        prefixFetchKeyUpdate,
        needStructure: true,
    };

    const { connectionId, dataConnection } = useGraphQLLoader(paramsLoader);

    useEffect(() => {
        dataStructureRef.current = dataConnection.structure.map((field: any) => ({
            ...getObjectFromRelayStoreById<{ id: string; key: string }>(environment, field.__id),
        }));

        const { columns } = getStructureColumn(
            dataStructureRef.current,
            columnsLabel!,
            isAddColumnAction,
            paramSorter,
            fixedColumn,
        );

        dataRowsRef.current = dataConnection.edges.map((edge) => {
            const row = {
                ...getObjectFromRelayStoreById<{ id: string; key: string }>(environment, edge.node.__id),
                key: edge.node.__id,
            };

            const newRow = getRowWithData(columns, row, '');

            const newChildren = getChildrenByExtendRow(row, 0, extendRow, extendRowByField, columns);

            if (newChildren) {
                (newRow as any).children = newChildren;
            }

            return newRow;
        });

        const newColumns = Array.isArray(columns) ? columns : [columns];

        const dataPageInfo = getObjectFromRelayStoreById<{ count: number }>(
            environment,
            (dataConnection.pageInfo as any).__id,
        );

        onUpdateData?.(dataRowsRef.current as any, dataPageInfo?.count!, connectionId, newColumns);
    }, [dataConnection, externalFetchKey]);

    return null;
};

export default TableDbLoader;
